import { faRandom } from '@fortawesome/free-solid-svg-icons'
import classNames from 'classnames'
import React from "react"
import { useBeforeunload } from 'react-beforeunload'
import { useDispatch } from "react-redux"
import { Button } from "reactstrap"
import { FightSegmentList } from 'web/components/fight-segments/FightSegmentList'
import { FaButton } from 'web/components/generic/FaButton'
import { FightHeader } from 'web/components/shared/FightHeader'
import { getFightCategoryLabelById } from 'web/data/gui-data'
import { FightSetup, FullFight } from 'web/data/models'
import { uniqueIdFromSpellAssignment } from 'web/engine/spell-engine'
import { EditFightActions, useEditFight_BadName } from 'web/redux/slices/edit-fight-slice'
import { EditSetupActions } from "web/redux/slices/edit-setup-slice"
import { InteractivityActions, useInteractivitySelectors } from 'web/redux/slices/interactivity-slice'
import { ModalActions } from 'web/redux/slices/modal-slice'
import { useSetupSelectors } from 'web/redux/slices/setup-slice'
import { decorateNameWithOrdinal } from 'web/utils/text-utils'
import { isSetupFightEvent } from 'web/utils/type-utils'
import { expectOneById } from 'web/utils/utils'
import { parseNewEvent, resolveEventMaxOffset } from '../edit-fight/EditFightContainer'
import { createEventAssignmentsCallback } from '../shared/utils'

type Props = {
    fullFight: FullFight
    setup: FightSetup
    onSave: () => void
    onCancel: () => void
    comparisonActive: boolean
}

export const EditSetupFightContainer: React.FC<Props> = ({
    fullFight,
    setup,
    onSave,
    onCancel,
    comparisonActive,
}) => {

    const disp = useDispatch()

    useBeforeunload(event => event.preventDefault())

    const setupStateSelectors = useSetupSelectors()
    const savedSetupsCount = setupStateSelectors.getSetupsForFight(fullFight.fight.id).length

    const [editFightSelectors] = useEditFight_BadName()

    const interactivitySelectors = useInteractivitySelectors()

    const eventAssignmentsCallback = createEventAssignmentsCallback({
        fullFight: fullFight,
        selectedSetup: setup,

        onHoverEnter: (({ setupId, availableEventIds }) => {
            disp(InteractivityActions.setAvailableEventIdsForSetup({
                setupId,
                availableEventIds
            }))
        }),

        onHoverLeave: ({ setupId }) => {
            disp(InteractivityActions
                .clearAvailableEventIdsForSetup({ setupId }))
        },

        onClick: (saUniqueId) => {
            disp(EditSetupActions.removeSpellAssignment({
                saUniqueId
            }))
        }
    })

    return (
        <div className="pr-3" style={{ width: "650px" }}>

            <FightHeader
                fightName={fullFight.fight.name}
                categoryName={getFightCategoryLabelById(fullFight.fight.categoryId)}
                className="mb-3"
            />

            <div className={classNames(
                "d-flex p-2 bg-light rounded border mb-3",
                "align-items-end justify-content-between",
                "space-mx-2"
            )}>
                <FaButton
                    icon={faRandom}
                    color={"primary"}
                    size="sm"
                    outline
                    disabled={savedSetupsCount <= 0}
                    title={savedSetupsCount <= 0 ? `Must have at least 1 saved setup to compare` : ''}
                    onClick={() => {
                        disp(EditSetupActions.toggleComparison({ comparisonActive: !comparisonActive }))
                    }}
                >
                    {"Compare"}
                </FaButton>

                <div className="space-mx-2">
                    <Button
                        color="success"
                        size="sm"
                        onClick={onSave}
                    >
                        {"Save changes"}
                    </Button>
                    <Button outline size="sm" onClick={onCancel}>
                        {"Cancel edits"}
                    </Button>
                </div>
            </div>

            <div className="space-my-3">
                <FightSegmentList
                    segments={fullFight.segments}
                    events={fullFight.events}
                    segmentProps={{
                        setupEditMode: {
                            onEditEvent: (eventId, segmentId) => {
                                const event = expectOneById(fullFight.events, eventId)

                                disp(ModalActions.openEditEventModal({
                                    event: event,
                                    segment: expectOneById(fullFight.segments, segmentId),
                                    setupEditMode: {
                                        setupFightEvent: isSetupFightEvent(event),
                                        onConvertToOutlineEvent: () => {

                                            if (isSetupFightEvent(event)) {
                                                disp(EditFightActions.convertSetupFightEventToOutlineEvent({
                                                    event: event
                                                }))
                                            } else {
                                                throw Error(`Tried to delete a non-setup fight event, this is not allowed`)
                                            }

                                        },
                                        onDeleteEvent: () => {

                                            if (isSetupFightEvent(event) === false) {
                                                throw Error(`Tried to delete a non-setup fight event, this is not allowed`)
                                            }

                                            const eventId = event.id

                                            const saRemove = setup.spellAssignments
                                                .filter(sa => sa.fightEventId === eventId)

                                            const taRemove = setup.teamAssignments
                                                .filter(sa => sa.fightEventId === eventId)

                                            disp(EditFightActions.deleteEvent({ eventId }))

                                            saRemove.forEach(sa => {
                                                disp(EditSetupActions.removeSpellAssignment({
                                                    saUniqueId: uniqueIdFromSpellAssignment(sa)
                                                }))
                                            })

                                            // TODO: Remove team
                                            // assignments too...

                                        }
                                    }
                                }))
                            },
                            onAddSetupEvent: ({ name, relativeTo, seconds, segment }) => {

                                const parseResult = parseNewEvent(
                                    segment,
                                    seconds,
                                    relativeTo,
                                    resolveEventMaxOffset(
                                        editFightSelectors.getChildSegmentOffset(segment)
                                    )
                                )

                                if (typeof parseResult === 'number') {
                                    disp(EditFightActions.createNewSetupEvent({
                                        fightOffset: parseResult,
                                        name,
                                        segmentId: segment.id,
                                        setupId: setup.id
                                    }))

                                    return undefined
                                }

                                return {
                                    errorMessage: parseResult
                                }
                            }
                        },

                        decorateNameWithOrdinal: (event) => {
                            return decorateNameWithOrdinal(fullFight.events, event)
                        },

                        eventProps: {
                            assignments: {
                                eventAssignments: eventAssignmentsCallback,
                                onAdd: (spellAssignment) => {
                                    disp(EditSetupActions.addSpellAssignment({
                                        spellAssignment
                                    }))
                                }
                            },
                            interactive: {
                                extraClassNames: (eventId: string) => {
                                    const eventState = interactivitySelectors
                                        .getEventStateForSetup(setup.id, eventId)

                                    if (eventState.valid) {
                                        return "bg-light-green"
                                    } else if (eventState.invalid) {
                                        return "bg-light-red"
                                    }
                                },
                            }
                        }
                    }}
                />
            </div>
        </div>
    )
}


